'use client';

import { ReactNode } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { EntityProceduresSkeleton } from './entity-procedures-skeleton.component';

import { Button } from '@/components/ui/button/button.component';
import { Card } from '@/components/ui/card.component';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs/tabs.component';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import {
  EntityProceduresChannelState,
  UseEntityProceduresReturn,
} from '@/app/(main)/(pages)/_components/entities/entity-procedure/_hooks/use-entity-procedures.hook';
import { ENTITY_PROCEDURE_SECTION_ID } from '@/app/(main)/(pages)/_components/entities/entity-procedure/entity-procedure.const';
import { SearchApiServiceChannel } from '@/app/(main)/(pages)/_types/search-api-response.type';
import { cn } from '@/utils/cn-tw-merge';

const CHANNEL_LABELS: Record<SearchApiServiceChannel, string> = {
  [SearchApiServiceChannel.IN_PERSON]: 'Presencial',
  [SearchApiServiceChannel.TELEHEALTH]: 'Teleconsulta',
};

const CHANNEL_TEST_IDS: Record<SearchApiServiceChannel, string> = {
  [SearchApiServiceChannel.IN_PERSON]: DataTestIdEnum.ENTITY_PROCEDURE_SECTION_TAB_IN_PERSON,
  [SearchApiServiceChannel.TELEHEALTH]: DataTestIdEnum.ENTITY_PROCEDURE_SECTION_TAB_TELEHEALTH,
};

interface EntityProceduresSectionProps<TGroup> {
  procedures: UseEntityProceduresReturn<TGroup>;
  title: string;
  renderProcedure: (procedure: TGroup, index: number) => ReactNode;
  className?: string;
  isScrollTarget?: boolean;
}

interface ChannelPanelProps<TGroup> {
  channel: EntityProceduresChannelState<TGroup>;
  isLoadingMore: boolean;
  renderProcedure: (procedure: TGroup, index: number) => ReactNode;
  onLoadMore: () => void;
  onRetry: () => void;
}

function ChannelPanel<TGroup>({
  channel,
  isLoadingMore,
  renderProcedure,
  onLoadMore,
  onRetry,
}: ChannelPanelProps<TGroup>) {
  if (channel.status === 'error') {
    return (
      <div className="flex flex-col items-center gap-4 py-6">
        <Typography
          role="status"
          variant={TypographyVariant.BODY_TEXT}
          className="text-center"
          data-testid={DataTestIdEnum.ENTITY_PROCEDURE_SECTION_ERROR}
        >
          Não foi possível carregar as consultas e horários.
        </Typography>

        <Button
          variant="secondary"
          className="w-fit"
          onClick={onRetry}
          data-testid={DataTestIdEnum.ENTITY_PROCEDURE_SECTION_RETRY}
        >
          Tentar novamente
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {channel.procedures.map(renderProcedure)}

      {channel.hasMore && (
        <Button
          variant="secondary"
          isLoading={isLoadingMore}
          onClick={onLoadMore}
          data-testid={DataTestIdEnum.ENTITY_PROCEDURE_SECTION_SEE_MORE}
        >
          Ver mais procedimentos
        </Button>
      )}
    </div>
  );
}

function SectionCard({
  title,
  className,
  isScrollTarget,
  children,
}: {
  title: string;
  className?: string;
  isScrollTarget: boolean;
  children: ReactNode;
}) {
  return (
    <Card
      id={isScrollTarget ? ENTITY_PROCEDURE_SECTION_ID : undefined}
      className={cn('flex w-full min-w-0 flex-col gap-4 scroll-mt-24 px-6 py-6', className)}
      data-testid={DataTestIdEnum.ENTITY_PROCEDURE_SECTION}
    >
      <Typography
        as="h2"
        variant={TypographyVariant.MEDIUM_SUBTITLE}
        className="text-center font-semibold text-gray-900"
        data-testid={DataTestIdEnum.ENTITY_PROCEDURE_SECTION_TITLE}
      >
        {title}
      </Typography>

      {children}
    </Card>
  );
}

export function EntityProceduresSection<TGroup>({
  procedures,
  title,
  renderProcedure,
  className,
  isScrollTarget = true,
}: EntityProceduresSectionProps<TGroup>) {
  const {
    activeChannel,
    availableChannels,
    selectChannel,
    channel,
    isLoading,
    isLoadingMore,
    loadMore,
    retry,
  } = procedures;

  const handleValueChange = (value: string) => selectChannel(value as SearchApiServiceChannel);

  if (isLoading) {
    return (
      <SectionCard title={title} className={className} isScrollTarget={isScrollTarget}>
        <EntityProceduresSkeleton />
      </SectionCard>
    );
  }

  if (availableChannels.length === 0) {
    return null;
  }

  return (
    <SectionCard title={title} className={className} isScrollTarget={isScrollTarget}>
      <Tabs value={activeChannel} onValueChange={handleValueChange} className="flex flex-col gap-4">
        <TabsList className="w-full" contentClassName="w-full">
          {availableChannels.map((serviceChannel) => (
            <TabsTrigger
              key={serviceChannel}
              value={serviceChannel}
              className="flex-1 text-base"
              data-testid={CHANNEL_TEST_IDS[serviceChannel]}
            >
              {CHANNEL_LABELS[serviceChannel]}
            </TabsTrigger>
          ))}
        </TabsList>

        {availableChannels.map((serviceChannel) => (
          <TabsContent key={serviceChannel} value={serviceChannel}>
            <ChannelPanel
              channel={channel}
              isLoadingMore={isLoadingMore}
              renderProcedure={renderProcedure}
              onLoadMore={loadMore}
              onRetry={retry}
            />
          </TabsContent>
        ))}
      </Tabs>
    </SectionCard>
  );
}
